import { motion } from 'framer-motion';
import { ArrowRight, Check, Diamond, Shield, Download, Zap, BadgeCheck, RefreshCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import CountdownTimer from '@/components/CountdownTimer';
import { useCheckout } from '@/components/CheckoutContext';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const FEATURES = [
  '4,000+ Production-Ready STL Files',
  '900GB+ Premium 3D Models',
  '8 Curated Category Folders',
  'Compatible with Rhino, ZBrush & Blender',
  '.STL, .OBJ & .STEP Formats Included',
  'Instant Download via Google Drive',
  'Commercial Use License',
  'Free Lifetime Updates',
];

export default function PricingSection() {
  const { open } = useCheckout();
  return (
    <section data-testid="pricing-section" className="relative py-20 sm:py-28" style={{ background: '#FFFFFF' }}>
      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8">
        <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-8">
          <span className="uppercase text-xs tracking-[0.25em] font-bold text-[#c88a5e] mb-4 block font-body" data-testid="pricing-overline">
            <Diamond className="inline-block w-3 h-3 mr-2 -mt-0.5" /> Get Instant Access
          </span>
        </motion.div>

        <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <CountdownTimer />
        </motion.div>

        <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="card-elevated-lg relative overflow-hidden" data-testid="pricing-card"
          style={{ boxShadow: '0 4px 12px rgba(253,195,161,0.08), 0 16px 48px rgba(0,0,0,0.06)' }}>
          {/* Top accent */}
          <div className="h-[3px] w-full" style={{ background: 'linear-gradient(90deg, transparent, #FDC3A1, transparent)' }} />

          <div className="px-6 sm:px-8 py-8 sm:py-10">
            <div className="text-center mb-8">
              <h3 className="font-heading font-bold text-[#111] text-xl sm:text-2xl mb-2" data-testid="pricing-card-title">Master Enterprise</h3>
              <p className="font-body text-sm text-[#888]">The complete DesignCarat bundle</p>
            </div>

            <div className="text-center mb-8" data-testid="pricing-card-price">
              <div className="flex items-baseline justify-center gap-3">
                <span className="font-mono text-lg text-[#bbb] line-through tabular-nums">₹34,999</span>
                <span className="font-heading font-black text-[#111] tabular-nums" style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>₹999</span>
              </div>
              <span className="font-body text-xs text-[#888] mt-2 block">One-time payment &bull; No subscription</span>
            </div>

            <div className="h-px w-full mb-8" style={{ background: 'rgba(0,0,0,0.06)' }} />

            <ul className="space-y-4 mb-10" data-testid="pricing-features-list">
              {FEATURES.map((feature, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`pricing-feature-${i}`}>
                  <span className="mt-0.5 w-5 h-5 flex items-center justify-center shrink-0 rounded-md" style={{ background: 'rgba(253,195,161,0.2)' }}>
                    <Check className="w-3 h-3 text-[#c88a5e]" />
                  </span>
                  <span className="font-body text-sm text-[#444]">{feature}</span>
                </li>
              ))}
            </ul>

            {/* ───────── Premium Trust Strip ───────── */}
            <div
              data-testid="pricing-trust-strip"
              className="mb-5 flex items-stretch justify-center rounded-full overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, rgba(253,195,161,0.10) 0%, rgba(255,247,205,0.6) 100%)',
                border: '1px solid rgba(253,195,161,0.45)',
                boxShadow:
                  '0 6px 18px -8px rgba(253,195,161,0.45), inset 0 1px 0 rgba(255,255,255,0.7)',
              }}
            >
              <Link
                to="/terms-of-service"
                data-testid="trust-strip-seller"
                className="group flex-1 flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 transition-all hover:bg-white/40"
              >
                <BadgeCheck
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#c88a5e] transition-transform duration-300 group-hover:scale-110"
                  strokeWidth={2.25}
                />
                <span className="font-body font-semibold text-[11.5px] sm:text-[13px] text-[#3d2614] tracking-tight">
                  Verified Indian Seller
                </span>
              </Link>

              <span
                aria-hidden="true"
                className="my-2 w-px"
                style={{ background: 'rgba(253,195,161,0.55)' }}
              />

              <Link
                to="/refund-policy"
                data-testid="trust-strip-refund"
                className="group flex-1 flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 transition-all hover:bg-white/40"
              >
                <RefreshCcw
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#c88a5e] transition-transform duration-500 group-hover:rotate-[-90deg]"
                  strokeWidth={2.25}
                />
                <span className="font-body font-semibold text-[11.5px] sm:text-[13px] text-[#3d2614] tracking-tight">
                  100% Refund Eligible
                </span>
              </Link>
            </div>

            <button data-testid="pricing-buy-button" onClick={() => open('pricing_cta')}
              className="btn-accent cta-pulse w-full inline-flex items-center justify-center gap-3 text-base sm:text-lg px-8 py-5 group">
              🚀 Grab Bundle Now &bull; ₹999
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            {/* trailing trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6" data-testid="pricing-trust-indicators">
              <span className="flex items-center gap-1.5 text-[#aaa] text-xs font-body"><Shield className="w-3.5 h-3.5" />Secure Checkout</span>
              <span className="flex items-center gap-1.5 text-[#aaa] text-xs font-body"><Download className="w-3.5 h-3.5" />Instant Delivery</span>
              <span className="flex items-center gap-1.5 text-[#aaa] text-xs font-body"><Zap className="w-3.5 h-3.5" />100% Safe &amp; Secure</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
